<template>
  <div class="stock-flow-page">
    <!-- 顶部导航 -->
    <header class="page-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ $t('wholesaler.product.StockFlowQuery.line6_text') }}</h1>
      <div class="header-right"></div>
    </header>

    <!-- 筛选区域 -->
    <div class="filter-section">
      <div class="filter-item">
        <label>{{ $t('common.products') }} <span class="required">*</span></label>
        <el-select
          v-model="selectedProductId"
          :placeholder="$t('wholesaler.product.StockFlowQuery.line16_placeholder')"
          filterable
          clearable
          @change="onProductChange"
          class="product-select"
        >
          <el-option
            v-for="product in productList"
            :key="product.id"
            :label="product.name"
            :value="product.id"
          />
        </el-select>
      </div>
    </div>

    <!-- 流水列表 -->
    <div class="flow-content">
      <div v-if="loading" class="loading-state">
        <div class="spinner">⏳</div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="!selectedProductId" class="empty-state">
        <p>{{ $t('wholesaler.product.StockFlowQuery.line40_text') }}</p>
      </div>

      <div v-else-if="flowList.length === 0" class="empty-state">
        <p>{{ $t('wholesaler.product.StockFlowQuery.line44_text') }}</p>
      </div>

      <div v-else class="flow-list">
        <div v-for="item in flowList" :key="item.id" class="flow-item">
          <div class="flow-header">
            <span class="flow-type" :class="item.adjustReason === '入库' ? 'in' : 'out'">
              {{ item.adjustReason }}
            </span>
            <span class="flow-date">{{ formatDate(item.createdAt) }}</span>
          </div>
          <div class="flow-body">
            <span class="flow-warehouse">{{ item.warehouseName || $t('wholesaler.product.StockFlowQuery.shopWarehouse') }}</span>
            <span class="flow-change" :class="item.adjustQuantity >= 0 ? 'positive' : 'negative'">
              {{ item.adjustQuantity >= 0 ? '+' : '' }}{{ item.adjustQuantity }}
            </span>
            <span class="flow-before-after">{{ item.beforeQuantity }} → {{ item.afterQuantity }}</span>
          </div>
          <div v-if="item.reason" class="flow-reason">{{ $t('wholesaler.product.StockFlowQuery.remarkLabel') }}{{ item.reason }}</div>
        </div>

        <!-- 分页 -->
        <div v-if="total > pageSize" class="pagination">
          <button
            :disabled="page <= 1"
            @click="changePage(page - 1)"
            class="page-btn"
          >{{ $t('common.prevPage') }}</button>
          <span class="page-info">{{ $t('wholesaler.product.StockFlowQuery.pageInfo', { page, totalPages }) }}</span>
          <button
            :disabled="page >= totalPages"
            @click="changePage(page + 1)"
            class="page-btn"
          >{{ $t('common.nextPage') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'StockFlowQuery',

  data() {
    return {
      productList: [],
      selectedProductId: null,
      flowList: [],
      loading: false,
      page: 1,
      pageSize: 20,
      total: 0
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    }
  },

  async mounted() {
    await this.loadProducts();
    // 从 URL query 中获取默认商品 ID
    const productId = this.$route.query.productId;
    if (productId) {
      this.selectedProductId = Number(productId);
      this.loadFlowRecords();
    }
  },

  methods: {
    async loadProducts() {
      try {
        const res = await api.get('/product/list', {
          params: { page: 1, pageSize: 200 }
        });
        if (Array.isArray(res)) {
          this.productList = res;
        } else if (res && res.data && Array.isArray(res.data)) {
          this.productList = res.data;
        } else if (res && res.content && Array.isArray(res.content)) {
          this.productList = res.content;
        } else {
          this.productList = [];
        }
      } catch (error) {
        console.error('加载商品列表失败:', error);
        this.productList = [];
      }
    },

    onProductChange() {
      this.page = 1;
      this.loadFlowRecords();
    },

    async loadFlowRecords() {
      if (!this.selectedProductId) {
        this.flowList = [];
        this.total = 0;
        return;
      }

      this.loading = true;
      try {
        const res = await api.get(`/inventory/product/${this.selectedProductId}/records`, {
          params: { page: this.page, pageSize: this.pageSize }
        });

        if (res && res.data && Array.isArray(res.data)) {
          this.flowList = res.data;
          this.total = res.total || 0;
        } else if (Array.isArray(res)) {
          this.flowList = res;
          this.total = res.length;
        } else {
          this.flowList = [];
          this.total = 0;
        }
      } catch (error) {
        console.error('加载库存流水失败:', error);
        this.flowList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    changePage(newPage) {
      if (newPage < 1 || newPage > this.totalPages) return;
      this.page = newPage;
      this.loadFlowRecords();
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    goBack() {
      this.$router.go(-1);
    }
  }
};
</script>

<style scoped>
.stock-flow-page {
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
}

.page-header h1 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.header-right {
  width: 36px;
}

.filter-section {
  background: #fff;
  padding: 16px;
  margin-bottom: 12px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-item label {
  font-size: 13px;
  font-weight: 600;
  color: #666;
  white-space: nowrap;
}

.required {
  color: #ff4d4f;
}

.product-select {
  flex: 1;
}

.flow-content {
  flex: 1;
  padding: 0 12px 80px;
}

.loading-state,
.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #999;
}

.spinner {
  font-size: 32px;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.flow-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.flow-item {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  border-left: 3px solid #e2e8f0;
}

.flow-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.flow-type {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}

.flow-type.in {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.flow-type.out {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.flow-date {
  font-size: 12px;
  color: #999;
}

.flow-body {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  flex-wrap: wrap;
}

.flow-warehouse {
  color: #666;
}

.flow-change {
  font-weight: 700;
}

.flow-change.positive {
  color: #10b981;
}

.flow-change.negative {
  color: #ef4444;
}

.flow-before-after {
  color: #999;
  font-size: 12px;
}

.flow-reason {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
  padding-left: 8px;
  border-left: 2px solid #e5e7eb;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
}

.page-btn {
  padding: 6px 14px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}

.page-btn:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.page-btn:not(:disabled):hover {
  border-color: var(--primary, #3498db);
  color: var(--primary, #3498db);
}

.page-info {
  font-size: 13px;
  color: #999;
}
</style>
